<template>
  <div class="list-customers-page">
    <PageHeader
      :title="t('myShopData.customer_groups.title')"
      summary=""
      :back-link-to="{ name: 'shop.system-objects' }"
      :back-link-text="t('navbar.systemData')"
    />

    <div class="page-content">
      <div class="ListsCustomers">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :show-reload-button="true"
          data-key="id"
          :enable-grouped-actions="true"
          :exportable="true"
          :show-export-all-button="true"
          :export-config="{ entityType: 'customer_groups' }"
          :persistent-filters="persistentFilters"
          @export="handleExport"
        >
          <!-- Composite column: group_info (name, id_group, date_modification) -->
          <template #cell-group_info="{ data }">
            <div class="fullname-cell">
              <div class="name-row">
                <span
                  v-if="cleanStr(data.name)"
                  class="contact-name"
                >
                  {{ cleanStr(data.name) }}
                </span>
                <span
                  v-else
                  class="contact-name contact-name--empty"
                >
                  {{ t('myShopData.customer_groups.noName') }}
                </span>
                <SpmBadge
                  :label="`#${data.group_id}`"
                  :colors="{ bg: '#e0f2fe', color: '#0369a1' }"
                  size="sm"
                  variant="soft"
                />
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: lang -->
          <template #cell-lang="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="cleanStr(data.lang)"
                v-tooltip.top="t(`languages.codes.${cleanStr(data.lang)}`)"
                class="lang-badge cursor-pointer"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: active -->
          <template #cell-active="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_active === 1 || data.is_active === '1'"
                v-tooltip.top="t('active')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('inactive')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>
        </spmtableapi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'CustomerGroupsV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/customers-groups',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'group_id',
        header: '',
        fields: [{
          name: 'group_id',
          label: t('myShopData.customer_groups.headers.id_group'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'group_info',
        header: t('myShopData.customer_groups.headers.name'),
        fields: [{
          name: 'name',
          label: t('myShopData.customer_groups.headers.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.customer_groups.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.customer_groups.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.customer_groups.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.customer_groups.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.customer_groups.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.customer_groups.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { value: 1, label: t('active') },
              { value: 0, label: t('inactive') },
            ],
          },
        }],
      },
    ];

    // Helper function to clean strings
    const cleanStr = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(value).trim();
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/customer-groups',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.customer_groups.export.success'));
        // } else {
        //   await showToastError(t('myShopData.customer_groups.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.customer_groups.export.error'));
      }
    };

    return {
      t,
      apiCall,
      columns,
      persistentFilters,
      cleanStr,
      handleExport,
    };
  },
});
</script>

<style scoped>
/* Group info composite column */
.fullname-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fullname-cell .name-row {
  display: flex;
  align-items: center;
}

.fullname-cell .contact-name {
  font-weight: 600;
  color: #1f2937;
}

.fullname-cell .contact-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.fullname-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

.group-id {
  display: flex;
  align-items: center;
  gap: 4px;
}

.group-id i {
  font-size: 0.625rem;
  color: #9ca3af;
}

.fullname-cell .date-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fullname-cell .date-info i {
  font-size: 0.625rem;
  color: #9ca3af;
}

/* Language badge */
.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

.cursor-pointer {
  cursor: pointer;
}
</style>

<style scoped lang="scss">
/* Page layout */
.list-customers-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
